import {omneticApi} from '@dms/api/base';

import {
  GetMyCalendarSelectedUsersApiResponse,
  SetMyCalendarSelectedUsersApiArg,
  SetMyCalendarSelectedUsersApiResponse,
  getMyCalendarSelectedUsersResponseSchema,
  setMyCalendarSelectedUsersRequestBodySchema,
  CompleteOAuth2AuthenticationApiArg,
  CompleteOAuth2AuthenticationApiResponse,
  OAuth2AuthenticationCallbackApiArg,
  OAuth2AuthenticationCallbackApiResponse,
  StartOAuth2AuthenticationApiArg,
  StartOAuth2AuthenticationApiResponse,
  completeOAuth2AuthenticationRequestBodySchema,
  startOAuth2AuthenticationRequestBodySchema,
  oAuth2AuthUrlResponseBodySchema,
  GetMyOutlookCalendarsApiResponse,
  getMyOutlookCalendarsResponseSchema,
  CreateCalendarIntegrationApiArg,
  CreateCalendarIntegrationApiResponse,
  GetCalendarIntegrationApiResponse,
  createCalendarIntegrationRequestBodySchema,
  getCalendarIntegrationResponseSchema,
  UpdateCalendarIntegrationApiArg,
  UpdateCalendarIntegrationApiResponse,
  updateCalendarIntegrationRequestBodySchema,
  RevokeCalendarIntegrationApiArg,
  RevokeCalendarIntegrationApiResponse,
} from './types';

export const calendarApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getMyCalendarSelectedUsers: build.query<GetMyCalendarSelectedUsersApiResponse, void>({
      query: () => ({
        url: `/dms/v1/calendar/selected-users`,
      }),
      providesTags: ['CalendarUsers'],
      extraOptions: {
        responseSchema: getMyCalendarSelectedUsersResponseSchema,
      },
    }),
    setMyCalendarSelectedUsers: build.mutation<
      SetMyCalendarSelectedUsersApiResponse,
      SetMyCalendarSelectedUsersApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar/selected-users`,
        method: 'PUT',
        body: queryArg.setMyCalendarSelectedUsersRequestBody,
      }),
      invalidatesTags: ['CalendarUsers'],
      extraOptions: {
        requestSchema: setMyCalendarSelectedUsersRequestBodySchema,
      },
    }),
    completeOAuth2Authentication: build.mutation<
      CompleteOAuth2AuthenticationApiResponse,
      CompleteOAuth2AuthenticationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration/oauth2/complete`,
        method: 'POST',
        body: queryArg.completeOAuth2AuthenticationRequestBody,
      }),
      extraOptions: {
        requestSchema: completeOAuth2AuthenticationRequestBodySchema,
      },
    }),
    oAuth2AuthenticationCallback: build.query<
      OAuth2AuthenticationCallbackApiResponse,
      OAuth2AuthenticationCallbackApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration/oauth2/callback`,
        params: {code: queryArg.code, state: queryArg.state, error: queryArg.error},
      }),
    }),
    startOAuth2Authentication: build.mutation<
      StartOAuth2AuthenticationApiResponse,
      StartOAuth2AuthenticationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration/oauth2/start`,
        method: 'POST',
        body: queryArg.startOAuth2AuthenticationRequestBody,
      }),
      extraOptions: {
        requestSchema: startOAuth2AuthenticationRequestBodySchema,
        responseSchema: oAuth2AuthUrlResponseBodySchema,
      },
    }),
    getMyOutlookCalendars: build.query<GetMyOutlookCalendarsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/outlook/calendars`,
      }),
      providesTags: ['OutlookCalendars'],
      extraOptions: {
        responseSchema: getMyOutlookCalendarsResponseSchema,
      },
    }),
    createCalendarIntegration: build.mutation<
      CreateCalendarIntegrationApiResponse,
      CreateCalendarIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration`,
        method: 'POST',
        body: queryArg.createCalendarIntegrationRequestBody,
      }),
      invalidatesTags: ['CalendarIntegration'],
      extraOptions: {
        requestSchema: createCalendarIntegrationRequestBodySchema,
      },
    }),
    getCalendarIntegration: build.query<GetCalendarIntegrationApiResponse, void>({
      query: () => ({
        url: `/dms/v1/calendar-integration`,
      }),
      providesTags: ['CalendarIntegration'],
      extraOptions: {
        responseSchema: getCalendarIntegrationResponseSchema,
      },
    }),
    updateCalendarIntegration: build.mutation<
      UpdateCalendarIntegrationApiResponse,
      UpdateCalendarIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration/${queryArg.integrationId}`,
        method: 'POST',
        body: queryArg.updateCalendarIntegrationRequestBody,
      }),
      invalidatesTags: ['CalendarIntegration'],
      extraOptions: {
        requestSchema: updateCalendarIntegrationRequestBodySchema,
      },
    }),
    revokeCalendarIntegration: build.mutation<
      RevokeCalendarIntegrationApiResponse,
      RevokeCalendarIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/calendar-integration/${queryArg.integrationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CalendarIntegration', 'OutlookCalendars'],
    }),
  }),
});

export const {
  useGetMyCalendarSelectedUsersQuery,
  useSetMyCalendarSelectedUsersMutation,
  useCompleteOAuth2AuthenticationMutation,
  useOAuth2AuthenticationCallbackQuery,
  useStartOAuth2AuthenticationMutation,
  useGetMyOutlookCalendarsQuery,
  useCreateCalendarIntegrationMutation,
  useGetCalendarIntegrationQuery,
  useUpdateCalendarIntegrationMutation,
  useRevokeCalendarIntegrationMutation,
} = calendarApi;
